if (!customElements.get('pdp-benefits')) {
  class PdpBenefits extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-benefit-item]'));
      this.list = this.querySelector('.pdp-benefits__list');
      this.image = this.querySelector('.pdp-benefits__image');
      this.activeItem = this.items.find((item) => item.classList.contains('is-active')) || this.items[0] || null;
      this.preloadedSrc = '';
      this.autoplayTimer = 0;
      this.sectionVisible = false;
      this.autoplayEnabled = this.dataset.autoplay !== 'false';
      this.intervalMs = this.readInterval();
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.image?.getAttribute('src')) {
        this.image.dataset.currentSrc = this.image.getAttribute('src');
      }

      this.onClick = (event) => {
        const item = event.target.closest('[data-benefit-item]');
        if (!item || !this.contains(item)) return;
        this.setActive(item);
        this.syncAutoplay();
      };

      this.onImageLoad = () => {
        this.image?.classList.remove('is-swapping');
      };

      this.onModeChange = () => this.syncAutoplay();
      this.onVisibility = () => this.syncAutoplay();

      this.list?.addEventListener('click', this.onClick);
      this.image?.addEventListener('load', this.onImageLoad);
      this.image?.addEventListener('error', this.onImageLoad);
      this.mobileQuery.addEventListener('change', this.onModeChange);
      this.motionQuery.addEventListener('change', this.onModeChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.bindSectionObserver();

      if (this.activeItem) this.preloadNext(this.activeItem);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.sectionObserver?.disconnect();
      this.sectionObserver = null;
      this.list?.removeEventListener('click', this.onClick);
      this.image?.removeEventListener('load', this.onImageLoad);
      this.image?.removeEventListener('error', this.onImageLoad);
      this.mobileQuery?.removeEventListener('change', this.onModeChange);
      this.motionQuery?.removeEventListener('change', this.onModeChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bindSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.sectionVisible = true;
        return;
      }

      this.sectionObserver = new IntersectionObserver((entries) => {
        this.sectionVisible = entries.some((entry) => entry.isIntersecting);
        this.syncAutoplay();
      });
      this.sectionObserver.observe(this);
    }

    readInterval() {
      const seconds = Number(this.dataset.autoplayInterval);
      if (!Number.isFinite(seconds) || seconds < 2) return 4000;
      return seconds * 1000;
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.mobileQuery.matches &&
        !this.motionQuery.matches &&
        this.sectionVisible &&
        document.visibilityState !== 'hidden' &&
        this.items.length > 1
      );
    }

    syncAutoplay() {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      this.autoplayTimer = window.setInterval(() => this.advance(), this.intervalMs);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    advance() {
      if (!this.canAutoplay()) {
        this.stopAutoplay();
        return;
      }

      const index = Math.max(0, this.items.indexOf(this.activeItem));
      const next = this.items[(index + 1) % this.items.length];
      this.setActive(next);
      this.reveal(next);
    }

    reveal(item) {
      if (!item) return;
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = item.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= viewport) return;
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    setActive(item) {
      if (!item || item === this.activeItem) return;
      this.activeItem = item;

      this.items.forEach((entry) => {
        const active = entry === item;
        entry.classList.toggle('is-active', active);
        entry.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.updateImage(item);
      this.preloadNext(item);
    }

    updateImage(item) {
      if (!this.image) return;
      if (item.dataset.title) this.image.alt = item.dataset.title;

      const src = item.dataset.imageSrc || '';
      if (!src || this.image.dataset.currentSrc === src) return;

      this.image.dataset.currentSrc = src;
      this.image.classList.add('is-swapping');
      this.image.src = src;
      this.image.srcset = item.dataset.imageSrcset || '';
      if (item.dataset.imageWidth) this.image.width = Number(item.dataset.imageWidth);
      if (item.dataset.imageHeight) this.image.height = Number(item.dataset.imageHeight);

      if (this.image.complete) this.image.classList.remove('is-swapping');
    }

    preloadNext(item) {
      const next = this.items[this.items.indexOf(item) + 1];
      const src = next?.dataset.imageSrc;
      if (!src || this.preloadedSrc === src) return;
      this.preloadedSrc = src;
      const preload = new Image();
      if (next.dataset.imageSrcset) preload.srcset = next.dataset.imageSrcset;
      preload.src = src;
    }
  }

  customElements.define('pdp-benefits', PdpBenefits);
}
